import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  FolderKanban, Users, BarChart3, CheckCircle2, ArrowRight,
  Zap, Shield, CalendarDays, Briefcase, PenTool, UserCheck,
  Building2, GraduationCap, Home, Wrench, MessageSquare, Lightbulb
} from 'lucide-react';

const BRAND = '#155179';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    { icon: FolderKanban, title: 'Gestion de projets', desc: 'Suivez chaque projet avec des phases détaillées et une progression automatique.' },
    { icon: Users, title: 'Gestion d\'équipe', desc: 'Assignez les responsables, suivez la charge de travail de chaque collaborateur.' },
    { icon: Zap, title: 'Actions automatiques', desc: 'Rappels et actions créés automatiquement selon l\'avancement des phases.' },
    { icon: BarChart3, title: 'Tableau de bord', desc: 'Visualisez vos KPIs, alertes et recommandations en un coup d\'oeil.' },
    { icon: Shield, title: 'Données sécurisées', desc: 'Chaque utilisateur n\'accède qu\'à ses propres données. Isolation stricte.' },
    { icon: CalendarDays, title: 'Planning intégré', desc: 'Visualisez vos échéances et actions à venir dans un calendrier clair.' },
  ];

  const profiles = [
    { icon: UserCheck, label: 'Assistant(e) administratif(ve)' },
    { icon: FolderKanban, label: 'Gestionnaire de projet' },
    { icon: PenTool, label: 'Freelance' },
    { icon: Building2, label: 'Entrepreneur / TPE' },
    { icon: GraduationCap, label: 'Coach / Formateur' },
    { icon: Home, label: 'Agent immobilier' },
    { icon: Wrench, label: 'Artisan' },
    { icon: MessageSquare, label: 'Community manager' },
    { icon: Lightbulb, label: 'Consultant' },
  ];

  const freeFeatures = [
    'Jusqu\'à 5 projets simultanés',
    'Suivi des phases',
    'Actions et rappels',
    'Tableau de bord',
  ];

  const proFeatures = [
    'Projets illimités',
    'Gestion d\'équipe complète',
    'Documents et fichiers',
    'Insights et analytics',
    'Sauvegarde et restauration',
    'Support prioritaire',
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: BRAND }}>
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-lg font-bold text-slate-900">CREA FLOW</span>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-600">
              Se connecter
            </Button>
            <Button onClick={() => navigate('/login?view=register')} style={{ backgroundColor: BRAND }} className="text-white hover:opacity-90">
              Essai gratuit
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full mb-8" style={{ backgroundColor: BRAND + '12', color: BRAND }}>
            <Zap className="w-4 h-4" />
            L'outil simple pour piloter vos projets et votre quotidien
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight tracking-tight">
            Organisez, suivez et pilotez<br />
            <span style={{ color: BRAND }}>vos projets</span><br />
            simplement
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            CREA FLOW centralise tout au même endroit pour suivre vos projets du début à la fin, pas à pas.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => navigate('/login?view=register')} style={{ backgroundColor: BRAND }} className="text-white hover:opacity-90 px-8 h-12 text-base" data-testid="hero-free-btn">
              Essayez gratuitement <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => window.location.href = 'https://crea-flow.com/decouvrir'} className="border-slate-300 text-slate-700 px-8 h-12 text-base" data-testid="hero-discover-btn">
              En savoir plus <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <span className="mt-3 text-sm text-slate-500 cursor-pointer hover:underline inline-block" onClick={() => navigate('/login')}>
            Déjà un compte ? Se connecter
          </span>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Tout ce dont vous avez besoin, au même endroit
            </h2>
            <p className="mt-3 text-base text-slate-600">
              Avec CREA FLOW vous pouvez :
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(({ icon: Icon, title, desc }) => (
              <Card key={title} className="p-6 hover:shadow-lg transition-all border-slate-200 text-center">
                <div className="w-12 h-12 rounded-lg mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: BRAND + '15' }}>
                  <Icon className="w-6 h-6" style={{ color: BRAND }} />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot sections */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto space-y-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-4">
                Suivez chaque projet en détail
              </h2>
              <p className="text-base text-slate-600 mb-6 leading-relaxed">
                Créez vos projets, définissez les phases et suivez la progression en temps réel. La barre de progression se met à jour automatiquement.
              </p>
              <ul className="space-y-2">
                {['Phases personnalisables', 'Progression automatique', 'Upload de fichiers'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: BRAND }} /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 shadow-lg overflow-hidden">
              <img src="/screenshots/project-detail.png" alt="Détail projet" className="w-full h-auto" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="order-2 md:order-1 rounded-xl border border-slate-200 shadow-lg overflow-hidden">
              <img src="/screenshots/projects.jpg" alt="Actions Projet" className="w-full h-auto" />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-4">
                Ne manquez plus aucune action
              </h2>
              <p className="text-base text-slate-600 mb-6 leading-relaxed">
                Filtrez par projet, phase, responsable ou statut. Changez le statut en un clic. Les rappels sont créés automatiquement.
              </p>
              <ul className="space-y-2">
                {['Filtres avancés', 'Actions automatiques', 'Assignation par collaborateur'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: BRAND }} /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Vous êtes... */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Vous êtes...</h2>
            <p className="mt-3 text-base text-slate-600">
              CREA FLOW s'adapte à tous les professionnels qui gèrent des projets au quotidien.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
            {profiles.map(({ icon: Icon, label }) => (
              <Card key={label} className="p-5 flex items-center gap-4 hover:shadow-md transition-shadow border-slate-200">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: BRAND + '12' }}>
                  <Icon className="w-5 h-5" style={{ color: BRAND }} />
                </div>
                <span className="text-sm font-medium text-slate-700">{label}</span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — Free + Pro */}
      <section className="py-16 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: BRAND }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
            Simple, transparent, sans engagement
          </h2>
          <p className="text-slate-300 text-base mb-10">
            Commencez gratuitement, passez au Pro quand vous le souhaitez.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free */}
            <Card className="p-8 text-center border-2 border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-1">Gratuit</h3>
              <p className="text-sm text-slate-500 mb-5">Pour démarrer et tester l'outil</p>
              <div className="mb-6">
                <span className="text-5xl font-bold text-slate-900">0€</span>
                <span className="text-slate-500"> /mois</span>
              </div>
              <ul className="space-y-3 text-left max-w-xs mx-auto mb-8">
                {freeFeatures.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: BRAND }} /> {f}
                  </li>
                ))}
              </ul>
              <Button size="lg" variant="outline" className="w-full h-12 text-base" onClick={() => navigate('/login?view=register')} data-testid="cta-free">
                Créer mon compte gratuit
              </Button>
            </Card>
            {/* Pro */}
            <Card className="p-8 text-center border-2" style={{ borderColor: BRAND }}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-slate-900">Pro</h3>
                <Badge className="text-xs text-white" style={{ backgroundColor: BRAND }}>Populaire</Badge>
              </div>
              <p className="text-sm text-slate-500 mb-5">Pour aller plus loin, sans limites</p>
              <div className="mb-6">
                <span className="text-5xl font-bold text-slate-900">9,99€</span>
                <span className="text-slate-500"> /mois</span>
              </div>
              <ul className="space-y-3 text-left max-w-xs mx-auto mb-8">
                {proFeatures.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: BRAND }} /> {f}
                  </li>
                ))}
              </ul>
              <Button size="lg" className="w-full h-12 text-base text-white hover:opacity-90" style={{ backgroundColor: BRAND }} onClick={() => navigate('/pricing')} data-testid="cta-pro">
                Passer au Pro <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-slate-500 mt-3">Paiement sécurisé par Stripe. Annulez à tout moment.</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: BRAND }}>
              <span className="text-white font-bold text-xs">C</span>
            </div>
            <span className="text-sm font-semibold text-slate-700">CREA FLOW</span>
          </div>
          <p className="text-sm text-slate-400">
            CREA FLOW par CREA DIGI. Tous droits réservés. <span className="ml-4 cursor-pointer hover:text-slate-600" onClick={() => navigate('/login')}>Support</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
